/**
 * Shared "Sign in with Google" helper for every auth surface.
 *
 * Google uses ONE button for both signup and login, so the branch point is
 * getAdditionalUserInfo(result).isNewUser: only a brand-new auth user gets the
 * signup bookkeeping (user doc, A/B fields, cohort counter, Klaviyo), and it
 * runs in the exact same order as the email/password signup handlers so the
 * two methods are indistinguishable downstream. A returning login performs
 * NONE of it. The caller receives { user, isNew } and runs its own
 * surface-specific continuation (navigation, project claiming, onAuthSuccess).
 */
import {
  signInWithPopup,
  linkWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
} from 'firebase/auth';
import firebase, { auth } from '../firebase';
import { addSubscriberToKlaviyo } from './klaviyo';
import {
  buildSignupAbFields,
  recordAbCohortAssignment,
  loadAbTestConfig,
  loadExcludedSignupEmails,
  AB_ENTRY_POINT_FIGMAFOLIO_WEB,
} from './abCohort';

// Map raw Firebase popup errors to a friendly Error the surfaces can show
// directly. `cancelled` marks user-initiated aborts (closing the popup) that
// surfaces should swallow silently instead of rendering as a failure.
function toFriendlyError(err) {
  const code = (err && err.code) || '';
  let message;
  let cancelled = false;
  switch (code) {
    case 'auth/popup-blocked':
      message = 'Your browser blocked the sign-in window. Allow popups for this site and try again.';
      break;
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      message = 'Sign-in was cancelled.';
      cancelled = true;
      break;
    case 'auth/unauthorized-domain':
      message = 'Google sign-in is not available on this domain yet. Please use your email and password.';
      break;
    case 'auth/account-exists-with-different-credential':
      message = 'An account already exists with this email. Sign in with your email and password instead.';
      break;
    case 'auth/credential-already-in-use':
      message = 'That Google account is already connected to a different account. Sign out first, then sign in with Google.';
      break;
    default:
      message = 'Google sign-in failed. Please try again.';
  }
  const friendly = new Error(message);
  friendly.code = code;
  friendly.cancelled = cancelled;
  return friendly;
}

/**
 * Run the Google popup flow and, for brand-new users only, the same signup
 * bookkeeping the email/password paths perform.
 *
 * If a user is already signed in, the popup LINKS Google to that account
 * (isNew always false, no signup bookkeeping) instead of signing in, so the
 * button can never switch accounts out from under a signed-in user.
 *
 * @param {Object} options
 * @param {string|null} options.entryPoint - pass 'figmafolio-web'
 *   (AB_ENTRY_POINT_FIGMAFOLIO_WEB) only from the main Figmafolio web auth
 *   surface, mirroring how MainAuth threads it into SignupPage. Every other
 *   surface passes nothing, so those signups are tagged with signUpProduct
 *   only and never receive a cohort or touch the funnel counters.
 * @returns {Promise<{user: import('firebase/auth').User, isNew: boolean, abFields: Object|null}>}
 *   abFields is the signup A/B fields object (signUpProduct and, when
 *   assigned, abCohort/abAssignedAt) for a brand-new user, null for a
 *   returning login. Callers use it to react to the just-assigned cohort
 *   (e.g. the forced migration screen) without re-reading the user doc.
 * @throws {Error} friendly error with .code and .cancelled (see above)
 */
export async function signInWithGoogle({ entryPoint = null } = {}) {
  // Always-safe guard: if someone is ALREADY signed in when a Google button
  // is clicked (rare, since auth pages redirect signed-in users, but the
  // plugin auth surfaces do not), never sign them into a different account.
  // Link Google to the current account instead (linkWithPopup adds the
  // method without dropping the existing one) and hand the caller its normal
  // signed-in continuation. provider-already-linked means there is nothing
  // to link: proceed as a plain signed-in navigation.
  if (auth.currentUser) {
    try {
      const linkResult = await linkWithPopup(auth.currentUser, new GoogleAuthProvider());
      return { user: linkResult.user, isNew: false, abFields: null };
    } catch (err) {
      if (err && err.code === 'auth/provider-already-linked') {
        return { user: auth.currentUser, isNew: false, abFields: null };
      }
      throw toFriendlyError(err);
    }
  }

  let result;
  try {
    result = await signInWithPopup(auth, new GoogleAuthProvider());
  } catch (err) {
    throw toFriendlyError(err);
  }

  const user = result.user;
  const isNew = getAdditionalUserInfo(result)?.isNewUser === true;
  const dbFirestore = firebase.firestore();
  let abFields = null;

  if (isNew) {
    // Same sequence and order as the email signup handlers (see
    // SignupPage.js onSubmit): config read, excluded-emails read only for the
    // figmafolio-web entry point, A/B fields, user doc, cohort counter,
    // Klaviyo. buildSignupAbFields gates cohort assignment identically to
    // email signups, so Google signups enter (or stay out of) the live A/B
    // test by the exact same rules.
    const isWebEntry = entryPoint === AB_ENTRY_POINT_FIGMAFOLIO_WEB;
    const abConfig = await loadAbTestConfig();
    const excludedEmails = isWebEntry ? await loadExcludedSignupEmails() : null;
    abFields = buildSignupAbFields(undefined, abConfig, {
      entryPoint,
      email: user.email,
      excludedEmails,
    });

    // This destructive .set() lives ONLY inside the isNew branch: running it
    // for a returning login would overwrite the existing user doc (resetting
    // subscriptionStatus, trialUsed and abCohort) and re-running the cohort
    // counter below would double-count the live funnel on every login.
    await dbFirestore.collection('user').doc(user.uid).set({
      name: user.displayName || '',
      email: user.email,
      signUpDate: new Date().toISOString(),
      trialUsed: false,
      subscriptionStatus: 'trial',
      signUpMethod: 'google',
      ...abFields,
    });

    // Best-effort cohort counter; never throws, so a failed stats write
    // cannot break account creation.
    recordAbCohortAssignment(abFields);

    try {
      await addSubscriberToKlaviyo(user.email, user.displayName || '');
    } catch (klaviyoErr) {
      // Klaviyo is non-critical, continue
    }
  } else {
    // Returning login: no writes at all. Defensive check only, so a user
    // whose first-run bookkeeping failed is at least visible in the console.
    try {
      const snap = await dbFirestore.collection('user').doc(user.uid).get();
      if (!snap.exists) {
        console.warn(
          '[googleAuth] returning Google user has no user doc (signup bookkeeping may have failed):',
          user.uid
        );
      }
    } catch (checkErr) {
      console.warn('[googleAuth] user doc existence check failed:', checkErr && checkErr.message);
    }
  }

  return { user, isNew, abFields };
}
